import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ContextSwitcher from './ContextSwitcher';

// ─── Design Tokens (matching App.js top bar) ──────────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  white:      '#FFFFFF',
  slate:      '#94A3B8',
  bg:         '#F8FAFC',
};

/**
 * PageLayout — shared wrapper for management pages (orgs, projects, apps, SCM).
 *
 * Provides the same dark navy top bar as the main App.js with:
 * - App title linking to home
 * - ContextSwitcher dropdown
 * - Navigation tabs for the current section
 * - Consistent page background
 */
export default function PageLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active nav item from current path
  const path = location.pathname;
  const isOrgs = path === '/orgs';
  const isProjects = path.includes('/projects') && !path.includes('/apps');
  const isApps = path.includes('/apps');
  const isSCM = path.includes('/scm-connections');

  const navItems = [
    { id: 'home', label: '📊 Dashboard', path: '/' },
    { id: 'orgs', label: '🏢 Organisations', path: '/orgs' },
    { id: 'agents', label: '🤖 Agents', path: '/agents' },
  ];

  const isActive = (item) => {
    if (item.id === 'home') return false; // Never highlight home on management pages
    if (item.id === 'orgs') return isOrgs || isProjects || isApps || isSCM;
    if (item.id === 'agents') return path === '/agents';
    return false;
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      {/* ── Top Navigation Bar ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        background: C.navy,
        padding: '0 20px',
      }}>
        <h1
          onClick={() => navigate('/')}
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 800,
            color: C.white,
            marginRight: 24,
            padding: '10px 0',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          ⚡ AI Modernization Hub
        </h1>
        <ContextSwitcher />
        <div style={{ flex: 1 }} />
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: isActive(item) ? 700 : 500,
              cursor: 'pointer',
              border: 'none',
              background: 'transparent',
              color: isActive(item) ? C.white : C.slate,
              borderBottom: isActive(item) ? `3px solid ${C.blue}` : '3px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* ── Breadcrumb Bar ─────────────────────────────────────────────────── */}
      <Breadcrumbs />

      {/* ── Page Content ───────────────────────────────────────────────────── */}
      <div style={{ padding: 0 }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Breadcrumbs — contextual breadcrumb trail based on current route.
 */
function Breadcrumbs() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const crumbs = [];

  if (path.startsWith('/orgs')) {
    crumbs.push({ label: 'Organisations', path: '/orgs' });

    if (path.includes('/projects') || path.includes('/scm-connections') || path.includes('/apps')) {
      // Extract orgId from path
      const parts = path.split('/');
      const orgIdx = parts.indexOf('orgs');
      const orgId = parts[orgIdx + 1];

      if (path.includes('/scm-connections')) {
        crumbs.push({ label: 'SCM Connections', path: `/orgs/${orgId}/scm-connections` });
      } else if (path.includes('/projects')) {
        crumbs.push({ label: 'Projects', path: `/orgs/${orgId}/projects` });
      }

      if (path.includes('/apps')) {
        const projIdx = parts.indexOf('projects');
        const projectId = parts[projIdx + 1];
        crumbs.push({ label: 'Projects', path: `/orgs/${orgId}/projects` });
        crumbs.push({ label: 'Applications', path: `/orgs/${orgId}/projects/${projectId}/apps` });
      }
    }
  }

  if (crumbs.length === 0) return null;

  return (
    <div style={{
      padding: '10px 32px',
      background: C.white,
      borderBottom: `1px solid #E2E8F0`,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
    }}>
      <span
        onClick={() => navigate('/')}
        style={{ color: C.blue, cursor: 'pointer', fontWeight: 500 }}
      >
        Home
      </span>
      {crumbs.map((crumb, i) => (
        <React.Fragment key={i}>
          <span style={{ color: '#94A3B8' }}>/</span>
          {i === crumbs.length - 1 ? (
            <span style={{ color: '#1E293B', fontWeight: 600 }}>{crumb.label}</span>
          ) : (
            <span
              onClick={() => navigate(crumb.path)}
              style={{ color: C.blue, cursor: 'pointer', fontWeight: 500 }}
            >
              {crumb.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
